/**
 * Privacy-First Analytics SDK for SafePsy
 *
 * Tracks anonymized events without storing sensitive data.
 * Supports both anonymous guests and wallet-authenticated users.
 * Works even if cookies are disabled (fallback to localStorage/memory).
 *
 * NEVER tracks:
 * - Therapy content, prompts, or AI replies
 * - Personal health information
 * - Raw wallet addresses or DID secrets
 * - Raw IP addresses
 * - Wallet identity (the client no longer computes or sends a wallet hash —
 *   the anonymous endpoint accepts only visitor/session keys, so no client-
 *   supplied identity can be asserted or brute-forced)
 *
 * ALWAYS tracks:
 * - Anonymous UUIDs (visitor_id, session_id)
 * - Timestamps, event names
 * - Coarse device/country info
 */

import { detectCountry } from './country-detection';
import { ga4Track } from './ga4';

// ============================================================================
// Constants
// ============================================================================

const VISITOR_ID_KEY = 'safepsy_vid';
const SESSION_ID_KEY = 'safepsy_sid';
const LAST_VISIT_KEY = 'safepsy_last_visit';
const VISITOR_ID_EXPIRY_MS = 31536000000; // 12 months
const SESSION_INACTIVITY_MS = 1800000; // 30 minutes
const DEDUPE_WINDOW_MS = 300000; // 5 minutes
const MAX_SENT_EVENTS_CACHE = 50;

/**
 * Events that should fire at most once per browser session regardless of how many
 * times the hook/callback is invoked (prevents double counts from re-renders).
 * All other events (messages, AI responses) fire per occurrence.
 */
const ONCE_PER_SESSION_EVENTS: ReadonlySet<string> = new Set([
  'flow_session_started',
  'cta_clicked',
  'return_within_24h',
  'did_creation_started',
  'history_save_started',
  'checkout_started',
]);

function isOncePerSessionEvent(eventName: string): boolean {
  return ONCE_PER_SESSION_EVENTS.has(eventName);
}
const API_ENDPOINT = '/api/analytics/event';
const FLUSH_DEBOUNCE_MS = 1000;

// ============================================================================
// Types
// ============================================================================

export type AnalyticsEventName =
  // Legacy (backward compatible)
  | 'landing_visit'
  | 'wallet_click'
  | 'wallet_success'
  | 'session_success'
  | 'did_success'
  | 'first_message_sent'
  | 'first_reply_received'
  | 'return_24h'
  // Product-analytics v2 (user-level funnel + confirmed events)
  | 'flow_session_started'
  | 'cta_clicked'
  | 'wallet_connect_started'
  | 'wallet_connect_succeeded'
  | 'auth_session_started'
  | 'guest_session_started'
  | 'message_submitted'
  | 'second_message_submitted'
  | 'ai_request_started'
  | 'ai_response_succeeded'
  | 'ai_response_failed'
  | 'ai_response_timeout'
  | 'did_creation_started'
  | 'did_creation_succeeded'
  | 'history_save_started'
  | 'history_save_succeeded'
  | 'history_retrieval_started'
  | 'history_retrieval_succeeded'
  | 'checkout_started'
  | 'payment_succeeded'
  | 'subscription_cancelled'
  | 'return_within_24h'
  // Onboarding tutorial (progressive coach)
  | 'onboarding_tour_started'
  | 'onboarding_step_viewed'
  | 'onboarding_tour_completed'
  | 'onboarding_tour_dismissed';

/**
 * Map internal analytics event names to GA4 event names.
 * Events listed here are tracked in both the self-hosted SDK and GA4.
 */
const ANALYTICS_TO_GA4: Record<string, string> = {
  landing_visit: 'page_view',
  wallet_click: 'wallet_connect_initiated',
  wallet_success: 'wallet_connected',
  session_success: 'session_started',
  did_success: 'did_minted',
  first_message_sent: 'first_chat_sent',
  first_reply_received: 'first_chat_replied',
  return_24h: 'returning_visit',
  flow_session_started: 'session_started_v2',
  cta_clicked: 'cta_clicked',
  wallet_connect_started: 'wallet_connect_started',
  wallet_connect_succeeded: 'wallet_connect_succeeded',
  message_submitted: 'message_submitted',
  second_message_submitted: 'second_message_submitted',
  ai_response_succeeded: 'ai_response_succeeded',
  ai_response_failed: 'ai_response_failed',
  did_creation_started: 'did_creation_started',
  did_creation_succeeded: 'did_creation_succeeded',
  history_save_started: 'history_save_started',
  history_save_succeeded: 'history_save_succeeded',
  history_retrieval_started: 'history_retrieval_started',
  history_retrieval_succeeded: 'history_retrieval_succeeded',
  checkout_started: 'checkout_started',
  return_within_24h: 'returning_visit',
  onboarding_tour_started: 'onboarding_tour_started',
  onboarding_step_viewed: 'onboarding_step_viewed',
  onboarding_tour_completed: 'onboarding_tour_completed',
  onboarding_tour_dismissed: 'onboarding_tour_dismissed',
};

export interface AnalyticsEvent {
  event_name: AnalyticsEventName;
  visitor_id: string;
  session_id: string;
  timestamp: string;
  page_url: string;
  referrer: string;
  user_agent: string;
  device_type: string;
  country: string;
  /** Client-generated idempotency key (equal retries reuse the same id and are deduped server-side). */
  client_event_id?: string;
  /** intent (client) vs confirmed (server/origin confirmed). Defaults to intent. */
  event_type?: 'intent' | 'confirmed';
  metadata?: Record<string, unknown>;
}

export interface AnalyticsConfig {
  enabled: boolean;
  debug: boolean;
  autoTrackPageViews: boolean;
}

// ============================================================================
// Storage Utilities
// ============================================================================

function getLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    const testKey = '__test_storage__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return localStorage;
  } catch {
    return null;
  }
}

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    const testKey = '__test_storage__';
    sessionStorage.setItem(testKey, 'test');
    sessionStorage.removeItem(testKey);
    return sessionStorage;
  } catch {
    return null;
  }
}

function getFromStorage<T>(storage: Storage | null, key: string): T | null {
  if (!storage) return null;
  try {
    const item = storage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch {
    return null;
  }
}

function setToStorage<T>(storage: Storage | null, key: string, value: T): void {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or unavailable
  }
}

function removeFromStorage(storage: Storage | null, key: string): void {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // Ignore
  }
}

// ============================================================================
// Identifier Management
// ============================================================================

/**
 * Generates a UUID v4 for anonymous visitor identification.
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Gets or creates a persistent visitor ID.
 * Survives refresh/revisit, expires after 12 months.
 */
function getOrCreateVisitorId(): string {
  const storage = getLocalStorage();
  if (storage) {
    const existing = getFromStorage<{ id: string; createdAt: number }>(storage, VISITOR_ID_KEY);
    if (existing && existing.id) {
      // Check if expired
      const age = Date.now() - existing.createdAt;
      if (age < VISITOR_ID_EXPIRY_MS) {
        return existing.id;
      }
      // Expired, remove and recreate
      removeFromStorage(storage, VISITOR_ID_KEY);
    }
  }

  // Create new visitor ID
  const newVisitor = {
    id: generateUUID(),
    createdAt: Date.now(),
  };

  setToStorage(storage, VISITOR_ID_KEY, newVisitor);
  return newVisitor.id;
}

/**
 * Gets or creates a session ID.
 * Renewed after 30 minutes of inactivity.
 */
function getOrCreateSessionId(): string {
  const storage = getSessionStorage();

  // Check memory first for session continuity
  const memStorage = getInMemoryStorage();
  let existing = getFromStorage<{ id: string; lastActivity: number }>(memStorage, SESSION_ID_KEY);

  if (!existing && storage) {
    existing = getFromStorage(storage, SESSION_ID_KEY);
  }

  if (existing && existing.id) {
    // Check inactivity timeout
    const inactiveTime = Date.now() - existing.lastActivity;
    if (inactiveTime < SESSION_INACTIVITY_MS) {
      // Update activity timestamp
      existing.lastActivity = Date.now();
      setToStorage(memStorage, SESSION_ID_KEY, existing);
      setToStorage(storage, SESSION_ID_KEY, existing);
      return existing.id;
    }
  }

  // Create new session ID
  const newSession = {
    id: generateUUID(),
    lastActivity: Date.now(),
  };

  setToStorage(memStorage, SESSION_ID_KEY, newSession);
  setToStorage(storage, SESSION_ID_KEY, newSession);
  return newSession.id;
}

/**
 * Gets the last visit timestamp for 24h return tracking.
 */
function getLastVisit(): number | null {
  const storage = getLocalStorage();
  const data = getFromStorage<number>(storage, LAST_VISIT_KEY);
  return data ?? null;
}

/**
 * Updates the last visit timestamp.
 */
function setLastVisit(): void {
  const storage = getLocalStorage();
  setToStorage(storage, LAST_VISIT_KEY, Date.now());
}

// ============================================================================
// In-Memory Storage (fallback)
// ============================================================================

const inMemoryStore: Record<string, unknown> = {};

function getInMemoryStorage(): Storage {
  return {
    getItem: (key: string) => {
      const item = inMemoryStore[key];
      return item !== undefined ? JSON.stringify(item) : null;
    },
    setItem: (key: string, value: string) => {
      try {
        inMemoryStore[key] = JSON.parse(value);
      } catch {
        inMemoryStore[key] = value;
      }
    },
    removeItem: (key: string) => {
      delete inMemoryStore[key];
    },
    clear: () => {
      Object.keys(inMemoryStore).forEach(key => delete inMemoryStore[key]);
    },
    get length() {
      return Object.keys(inMemoryStore).length;
    },
    key: (index: number) => Object.keys(inMemoryStore)[index] ?? null,
  };
}

// ============================================================================
// Deduplication
// ============================================================================

const sentEventsCache = new Map<string, number>();

function getDedupeKey(eventName: string, sessionId: string): string {
  return `${eventName}_${sessionId}`;
}

function isDuplicateEvent(eventName: string, sessionId: string): boolean {
  const key = getDedupeKey(eventName, sessionId);
  const timestamp = sentEventsCache.get(key);

  if (timestamp !== undefined) {
    const age = Date.now() - timestamp;
    if (age < DEDUPE_WINDOW_MS) {
      return true;
    }
    // Remove expired entry
    sentEventsCache.delete(key);
  }

  return false;
}

function markEventAsSent(eventName: string, sessionId: string): void {
  const key = getDedupeKey(eventName, sessionId);

  // Clean up old entries if cache is full
  if (sentEventsCache.size >= MAX_SENT_EVENTS_CACHE) {
    const now = Date.now();
    for (const [k, ts] of sentEventsCache.entries()) {
      if (now - ts > DEDUPE_WINDOW_MS) {
        sentEventsCache.delete(k);
      }
    }
  }

  sentEventsCache.set(key, Date.now());
}

// ============================================================================
// Event Queue & Retry Logic
// ============================================================================

interface QueuedEvent {
  event: AnalyticsEvent;
  attempts: number;
  nextRetry: number;
}

const eventQueue: QueuedEvent[] = [];
let flushTimeoutId: ReturnType<typeof setTimeout> | null = null;
let isFlushing = false;

const MAX_RETRY_ATTEMPTS = 3;
const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

function calculateBackoff(attempts: number): number {
  const backoff = BASE_BACKOFF_MS * Math.pow(2, attempts);
  return Math.min(backoff, MAX_BACKOFF_MS);
}

function queueEvent(event: AnalyticsEvent): void {
  eventQueue.push({
    event,
    attempts: 0,
    nextRetry: Date.now(),
  });

  scheduleFlush();
}

function scheduleFlush(): void {
  if (flushTimeoutId) {
    clearTimeout(flushTimeoutId);
  }

  flushTimeoutId = setTimeout(() => {
    flushTimeoutId = null;
    void flushEvents();
  }, FLUSH_DEBOUNCE_MS);
}

async function flushEvents(): Promise<void> {
  if (isFlushing || eventQueue.length === 0) {
    return;
  }

  isFlushing = true;
  const now = Date.now();

  // Get events ready to send
  const readyEvents = eventQueue.filter(e => e.nextRetry <= now);

  if (readyEvents.length === 0) {
    isFlushing = false;
    scheduleFlush();
    return;
  }

  // Remove ready events from queue
  const remainingEvents = eventQueue.filter(e => e.nextRetry > now);
  eventQueue.length = 0;
  eventQueue.push(...remainingEvents);

  try {
    // Use sendBeacon if available for better delivery
    const payload = JSON.stringify(readyEvents.map(e => e.event));

    if (navigator.sendBeacon && typeof document !== 'undefined') {
      const blob = new Blob([payload], { type: 'application/json' });
      const sent = navigator.sendBeacon(API_ENDPOINT, blob);

      if (sent) {
        // Success - don't retry
      } else {
        // Failed - requeue with backoff
        requeueEvents(readyEvents);
      }
    } else {
      // Fallback to fetch with keepalive
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      });

      if (response.ok || response.status === 204) {
        // Success
      } else if (response.status >= 500) {
        // Server error - retry
        requeueEvents(readyEvents);
      }
      // 4xx errors - don't retry
    }
  } catch (error) {
    // Network error - retry
    requeueEvents(readyEvents);
  }

  isFlushing = false;

  // Schedule next flush if there are still events
  if (eventQueue.length > 0) {
    scheduleFlush();
  }
}

function requeueEvents(events: QueuedEvent[]): void {
  for (const queued of events) {
    queued.attempts++;

    if (queued.attempts >= MAX_RETRY_ATTEMPTS) {
      // Give up after max retries
      continue;
    }

    queued.nextRetry = Date.now() + calculateBackoff(queued.attempts);
    eventQueue.push(queued);
  }
}

// ============================================================================
// Device & Location Detection
// ============================================================================

function getDeviceType(): string {
  if (typeof navigator === 'undefined') {
    return 'unknown';
  }

  const ua = navigator.userAgent;

  // Mobile detection
  if (/Android/i.test(ua) || /iPhone/i.test(ua) || /iPad/i.test(ua) || /iPod/i.test(ua) || /webOS/i.test(ua) || /BlackBerry/i.test(ua) || /Opera Mini/i.test(ua) || /IEMobile/i.test(ua)) {
    return 'mobile';
  }

  // Tablet detection
  if (/iPad/i.test(ua) || /Android(?!.*Mobile)/i.test(ua) || /Tablet/i.test(ua)) {
    return 'tablet';
  }

  // Desktop
  return 'desktop';
}

// ============================================================================
// Main Analytics Class
// ============================================================================

class AnalyticsSDK {
  private config: AnalyticsConfig = {
    enabled: true,
    debug: false,
    autoTrackPageViews: false,
  };

  private initialized = false;

  /**
   * Initialize the analytics SDK.
   */
  initialize(config?: Partial<AnalyticsConfig>): void {
    if (this.initialized) {
      return;
    }

    this.config = {
      ...this.config,
      ...config,
    };

    // Initialize visitor and session IDs
    getOrCreateVisitorId();
    getOrCreateSessionId();

    this.initialized = true;
  }

  /**
   * Check if analytics consent is given.
   */
  private hasConsent(): boolean {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }

    try {
      const consent = localStorage.getItem('cookieConsent');
      if (!consent) return false;

      const preferences = JSON.parse(consent) as { analytics?: boolean };
      return preferences.analytics === true;
    } catch {
      return false;
    }
  }

  /**
   * Track an analytics event.
   *
   * Dedup policy:
   * - `ONCE_PER_SESSION_EVENTS` (milestones like flow_session_started / return_within_24h)
   *   are deduped client-side per session for a 5-minute window.
   * - Every other event fires on each occurrence, but carries a stable `client_event_id`
   *   per logical event so retries (sendBeacon fallback, flush retry) are idempotent
   *   server-side while distinct occurrences remain distinct.
   */
  async trackEvent(
    eventName: AnalyticsEventName,
    metadata?: Record<string, unknown>,
    options?: { force?: boolean; eventType?: 'intent' | 'confirmed' }
  ): Promise<void> {
    // Check consent unless forced
    if (!options?.force && !this.hasConsent()) {
      return;
    }

    if (!this.config.enabled) {
      return;
    }

    const sessionId = getOrCreateSessionId();

    // Milestone events: collapse accidental duplicate fires within a short window.
    if (isOncePerSessionEvent(eventName)) {
      if (isDuplicateEvent(eventName, sessionId)) {
        return;
      }
      markEventAsSent(eventName, sessionId);
    }

    const visitorId = getOrCreateVisitorId();
    const timestamp = new Date().toISOString();

    // Get page info
    const pageUrl = typeof window !== 'undefined' ? window.location.pathname : '';
    const referrer = typeof document !== 'undefined' ? document.referrer : '';
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';

    // Distinguish intentional once-per-session ids from per-occurrence ids.
    const clientEventId = isOncePerSessionEvent(eventName)
      ? `milestone:${eventName}:${sessionId}:${Math.floor(Date.now() / 300000)}`
      : crypto.randomUUID();

    // Build event
    const event: AnalyticsEvent = {
      event_name: eventName,
      visitor_id: visitorId,
      session_id: sessionId,
      timestamp,
      page_url: pageUrl,
      referrer,
      user_agent: userAgent,
      device_type: getDeviceType(),
      country: detectCountry(),
      event_type: options?.eventType ?? 'intent',
      client_event_id: clientEventId,
      metadata: metadata ?? {},
    };

    // Queue for sending
    queueEvent(event);

    // Parallel track: fire GA4 event (silently no-ops if not loaded)
    const ga4EventName = ANALYTICS_TO_GA4[eventName];
    if (ga4EventName) {
      ga4Track(ga4EventName, {
        ...metadata,
        event_category: 'engagement',
        event_label: eventName,
      });
    }
  }

  /**
   * Track landing visit (call on app mount).
   * Also fires a GA4 page_view.
   */
  async trackLandingVisit(): Promise<void> {
    // Check for 24h return
    const lastVisit = getLastVisit();
    const now = Date.now();

    if (lastVisit !== null) {
      const deltaMs = now - lastVisit;
      const MIN_GAP_MS = 60000; // 1 minute
      const WINDOW_MS = 86400000; // 24 hours

      if (deltaMs >= MIN_GAP_MS && deltaMs <= WINDOW_MS) {
        await this.trackEvent('return_24h');
      }
    }

    // Update last visit (only with consent — this creates a persistent identifier)
    if (this.hasConsent()) {
      setLastVisit();
    }

    // Track landing
    await this.trackEvent('landing_visit', {
      landing_page: typeof window !== 'undefined' ? window.location.pathname : '/',
      utm_source: this.getUtmParam('utm_source'),
      utm_campaign: this.getUtmParam('utm_campaign'),
      utm_medium: this.getUtmParam('utm_medium'),
    });

    // GA4 page_view fires inside trackEvent via the mapping above
  }

  /**
   * Get UTM parameter from URL.
   */
  private getUtmParam(param: string): string | undefined {
    if (typeof window === 'undefined' || !window.location.search) {
      return undefined;
    }

    const params = new URLSearchParams(window.location.search);
    return params.get(param) ?? undefined;
  }

  /**
   * Track a consent-backed product session start (the v2 funnel first stage).
   * Call once per app mount alongside trackLandingVisit.
   */
  async trackFlowSessionStart(): Promise<void> {
    await this.trackEvent('flow_session_started', {
      landing_page: typeof window !== 'undefined' ? window.location.pathname : '/',
      utm_source: this.getUtmParam('utm_source'),
      utm_campaign: this.getUtmParam('utm_campaign'),
      utm_medium: this.getUtmParam('utm_medium'),
      utm_content: this.getUtmParam('utm_content'),
      utm_term: this.getUtmParam('utm_term'),
    });
  }

  /**
   * Track a user message submission (per-occurrence; only the first per session
   * contributes to the funnel, but all are counted for engagement).
   * @param isSecondOrLater whether this is the second+ message in the session
   */
  async trackMessageSubmitted(isSecondOrLater = false): Promise<void> {
    await this.trackEvent('message_submitted', { conversation_mode: 'chat' });
    if (isSecondOrLater) {
      await this.trackEvent('second_message_submitted', { conversation_mode: 'chat' });
    }
  }

  /**
   * Track an AI response that reached the client (client-side intent proxy; the
   * server emits the authoritative ai_response_succeeded).
   */
  async trackAiResponseReceived(latencyMs?: number): Promise<void> {
    await this.trackEvent('ai_response_succeeded', {
      latency_ms: latencyMs,
      error_category: 'none',
    });
  }

  /**
   * Track wallet connection events (v2 names, distinct from legacy wallet_click/wallet_success).
   */
  async trackWalletConnectStarted(provider?: string): Promise<void> {
    await this.trackEvent('wallet_connect_started', {
      wallet_provider: provider,
    });
  }

  async trackWalletConnectSucceeded(provider?: string, chainId?: number): Promise<void> {
    await this.trackEvent('wallet_connect_succeeded', {
      wallet_provider: provider,
      chain_id: chainId,
    });
  }

  /**
   * Track a DID creation attempt (client intent; success is confirmed by the
   * did service / on-chain check).
   */
  async trackDidCreationStarted(): Promise<void> {
    await this.trackEvent('did_creation_started', {});
  }

  async trackHistorySaveStarted(): Promise<void> {
    await this.trackEvent('history_save_started', { operation_type: 'save' });
  }

  async trackHistoryRetrievalStarted(): Promise<void> {
    await this.trackEvent('history_retrieval_started', { operation_type: 'retrieve' });
  }

  async trackHistoryRetrievalSucceeded(): Promise<void> {
    await this.trackEvent('history_retrieval_succeeded', { operation_type: 'retrieve' });
  }

  async trackCheckoutStarted(plan?: string, priceCents?: number, billingPeriod?: string): Promise<void> {
    await this.trackEvent('checkout_started', {
      plan,
      price_cents: priceCents,
      billing_period: billingPeriod,
    });
  }

  /**
   * Onboarding tutorial coach events (progressive guided steps).
   */
  async trackOnboardingStarted(): Promise<void> {
    await this.trackEvent('onboarding_tour_started', {});
  }

  async trackOnboardingStepViewed(step: number): Promise<void> {
    await this.trackEvent('onboarding_step_viewed', { step: String(step) });
  }

  async trackOnboardingCompleted(): Promise<void> {
    await this.trackEvent('onboarding_tour_completed', {});
  }

  async trackOnboardingDismissed(step: number): Promise<void> {
    await this.trackEvent('onboarding_tour_dismissed', { step: String(step) });
  }

  /**
   * Check if analytics SDK is enabled.
   */
  get isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Enable analytics.
   * Also attempts to initialise GA4 if consent permits.
   */
  async enable(): Promise<void> {
    this.config.enabled = true;
  }

  /**
   * Disable analytics.
   */
  disable(): void {
    this.config.enabled = false;
  }

  /**
   * Set debug mode.
   */
  setDebug(debug: boolean): void {
    this.config.debug = debug;
  }

  /**
   * Flush pending events immediately.
   */
  async flush(): Promise<void> {
    await flushEvents();
  }

  /**
   * Get current visitor ID (for debugging).
   */
  getVisitorId(): string {
    return getOrCreateVisitorId();
  }

  /**
   * Get current session ID (for debugging).
   */
  getSessionId(): string {
    return getOrCreateSessionId();
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const analytics = new AnalyticsSDK();

// Auto-initialize on import (optional)
if (typeof window !== 'undefined') {
  analytics.initialize();
}

// ============================================================================
// Convenience Functions
// ============================================================================

export const trackEvent = analytics.trackEvent.bind(analytics);
export const trackLandingVisit = analytics.trackLandingVisit.bind(analytics);
export const trackFlowSessionStart = analytics.trackFlowSessionStart.bind(analytics);
export const trackMessageSubmitted = analytics.trackMessageSubmitted.bind(analytics);
export const trackAiResponseReceived = analytics.trackAiResponseReceived.bind(analytics);
export const trackWalletConnectStarted = analytics.trackWalletConnectStarted.bind(analytics);
export const trackWalletConnectSucceeded = analytics.trackWalletConnectSucceeded.bind(analytics);
export const trackDidCreationStarted = analytics.trackDidCreationStarted.bind(analytics);
export const trackHistorySaveStarted = analytics.trackHistorySaveStarted.bind(analytics);
export const trackHistoryRetrievalStarted = analytics.trackHistoryRetrievalStarted.bind(analytics);
export const trackHistoryRetrievalSucceeded = analytics.trackHistoryRetrievalSucceeded.bind(analytics);
export const trackCheckoutStarted = analytics.trackCheckoutStarted.bind(analytics);
export const trackOnboardingStarted = analytics.trackOnboardingStarted.bind(analytics);
export const trackOnboardingStepViewed = analytics.trackOnboardingStepViewed.bind(analytics);
export const trackOnboardingCompleted = analytics.trackOnboardingCompleted.bind(analytics);
export const trackOnboardingDismissed = analytics.trackOnboardingDismissed.bind(analytics);
